import React, { useEffect, useState } from 'react';
import './TournamentPage.css';
import { useNavigate } from 'react-router-dom';
import { getLoggedInUser } from '../logic/loginLogic';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { updateDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  collection,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { playSound } from '../utils/soundPlayer';
import { handleJoinTournament } from '../utils/tournamentUtils';
import { normalizePhoneNumber, validatePhoneNumberUniqueness, validatePhoneNumberFormat } from '../utils/phoneValidation';

const TournamentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [joinedTournamentIds, setJoinedTournamentIds] = useState([]);
  const [confirmJoinTournament, setConfirmJoinTournament] = useState(null);
  const [liveLink, setLiveLink] = useState('');
  const [ongoingLink, setOngoingLink] = useState('');
  const [lastLink, setLastLink] = useState('');
  const [showLive, setShowLive] = useState(false);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showDpModal, setShowDpModal] = useState(false);
  const [missingDp, setMissingDp] = useState(0);
  const [showMvolaModal, setShowMvolaModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [showDpConfirmModal, setShowDpConfirmModal] = useState(false);
  const [confirmTournament, setConfirmTournament] = useState(null);

  const isNativePlatform = (() => {
    try {
      return !!Capacitor?.isNativePlatform?.();
    } catch {
      return false;
    }
  })();

  const openExternalUrl = async (url) => {
    if (!url) return;
    try {
      const browser = Capacitor?.Plugins?.Browser;
      if (browser?.open) {
        await browser.open({ url });
        return;
      }
    } catch {
      // ignore and fall back
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  function toFirestoreTimestampAny(value, { Timestamp, serverTimestamp }) {
    if (value && typeof value.toDate === 'function') return value;
    if (value instanceof Date && !isNaN(value.getTime())) return Timestamp.fromDate(value);
    if (typeof value === 'string') {
      const normalized = value.trim().replace(/\//g, '-');
      const d = new Date(normalized);
      if (!isNaN(d.getTime())) return Timestamp.fromDate(d);
    }
    return serverTimestamp();
  }

  useEffect(() => {
    const u = getLoggedInUser();
    if (!u) {
      navigate('/');
    } else {
      setUser(u);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'tournament-v2', 'upcoming', 'list'));
        let tournamentsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        tournamentsList = tournamentsList.sort((a, b) => new Date(a.date) - new Date(b.date));
        setTournaments(tournamentsList);

        const userSnapshot = await getDocs(collection(db, 'users', user.uid, 'tournaments'));
        const ids = userSnapshot.docs.map(doc => doc.id);
        setJoinedTournamentIds(ids);

        const linksDoc = await getDoc(doc(db, 'tournament-v2', 'links'));
        if (linksDoc.exists()) {
          const data = linksDoc.data();
          setOngoingLink(data.ongoing || '');
          setLastLink(data.last || '');
          setLiveLink(data.Live || '');
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    fetchData();
  }, [user]);

  const handleJoinTournamentClick = async (tournament) => {
    console.log('🏆 Starting tournament join process for:', tournament.title);

    if (!user) {
      console.error('❌ No user found');
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userRef);
    const userData = userDocSnap.data() || {};
    const userDP = Number(userData.dp ?? 0);
    const dpPrice = Number(tournament.dpPrice ?? 0);

    console.log('💰 DP Check - User DP:', userDP, 'Tournament Price:', dpPrice);

    if (userDP < dpPrice) {
      console.log('❌ Insufficient DP, showing modal');
      setMissingDp(dpPrice - userDP);
      setSelectedTournament(tournament);
      setShowDpModal(true);
      return;
    }

    console.log('✅ DP sufficient, proceeding with join...');
    setJoining(true);

    try {
      console.log('📤 Calling handleJoinTournament utility...');
      const result = await handleJoinTournament(
        tournament,
        (successResult) => {
          console.log('✅ Tournament join successful:', successResult);
          setJoinedTournamentIds(prev => prev.includes(tournament.id) ? prev : [...prev, tournament.id]);
          setConfirmJoinTournament(null);
          toast.success(`✅ You joined "${tournament.title}"! -${dpPrice} DP`);

          if (successResult.newDP !== undefined) {
            console.log('New DP balance:', successResult.newDP);
          }
        },
        (error) => {
          console.error('❌ Tournament join error in callback:', error);
          toast.error(`❌ Failed to join tournament: ${error.message || error}`);
        }
      );
      console.log('📥 Tournament join result:', result);
    } catch (error) {
      console.error('❌ Tournament join error in try/catch:', error);
      toast.error(`❌ Failed to join tournament: ${error.message || error}`);
    } finally {
      console.log('🏁 Tournament join process finished');
      setJoining(false);
    }
  };

  const handleMvolaTournamentPayment = async () => {
    if (!selectedTournament || !phoneNumber.trim()) {
      alert('Please enter a valid phone number');
      return;
    }

    // Validate phone number format
    if (!validatePhoneNumberFormat(phoneNumber)) {
      alert('Invalid phone number format. Please use format: 0341234567');
      return;
    }

    // Validate that phone number is not registered to another user
    const normalizedInputPhone = normalizePhoneNumber(phoneNumber);
    const uniquenessResult = await validatePhoneNumberUniqueness(normalizedInputPhone, user.uid);
    
    if (!uniquenessResult.isUnique) {
      alert(`Security Verification Failed: ${uniquenessResult.message}. Please use a different phone number.`);
      return;
    }

    setIsPaymentProcessing(true);

    try {
      console.log('🏆 Starting MVola tournament payment:', {
        tournament: selectedTournament.title,
        dpPrice: selectedTournament.dpPrice,
        phone: phoneNumber
      });

      const response = await fetch('https://us-central1-yme-dp-manager.cloudfunctions.net/mvolaTournamentPayment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          tournamentId: selectedTournament.id,
          tournamentData: selectedTournament,
          phone: phoneNumber.trim()
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowPhoneModal(false);
        setShowMvolaModal(false);
        setShowDpModal(false);
        setPhoneNumber('');

        toast.success(`✅ Tournament payment initiated! Please approve the transaction in MVola to complete your registration.`);
        toast.info(`🏆 Tournament: ${selectedTournament.title} - Payment pending approval...`);

        startTournamentPaymentPolling(result.referenceId, selectedTournament);

      } else {
        toast.error(`❌ Payment failed: ${result.message}`);
      }
    } catch (error) {
      console.error('❌ MVola tournament payment error:', error);
      toast.error('❌ Payment failed. Please try again.');
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  const startTournamentPaymentPolling = (referenceId, tournament) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch('https://us-central1-yme-dp-manager.cloudfunctions.net/checkTransactionStatus', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referenceId })
        });

        const result = await response.json();

        if (result.success && result.status === 'Completed') {
          clearInterval(pollInterval);
          toast.success(`🎉 Tournament registration successful! You are now registered for "${tournament.title}"`);

          setJoinedTournamentIds(prev => prev.includes(tournament.id) ? prev : [...prev, tournament.id]);

        } else if (result.success && result.status === 'Failed') {
          clearInterval(pollInterval);
          toast.error(`❌ Tournament payment failed. Please try again.`);
        }
      } catch (error) {
        console.error('❌ Tournament payment polling error:', error);
      }
    }, 5000);

    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  return (
    <div className="tournament-page">
      <div className="tournament-header">
        <div className="tournament-header-content">
          <h1 className="tournament-page-title">{t('tournament.upcoming')}</h1>
        </div>
      </div>

      <button className="back-button" onClick={() => {
        playSound('return');
        navigate('/menu');
      }}>
        {t('tournament.back')}
      </button>

      <div className="tournament-main-container">
        {tournaments.length === 0 && (
          <div className="empty-tournaments">
            <p>{t('tournament.noUpcoming')}</p>
          </div>
        )}

        {tournaments.map((tournament) => (
          <div key={tournament.id} className="tournament-card">
            <div className="tournament-card-header">
              <h2 className="tournament-card-title">{tournament.title}</h2>
              {tournament.started && (
                <span className="tournament-status-badge started">{t('tournament.startedWarning')}</span>
              )}
            </div>

            {tournament.bannerUrl && (
              <div className="tournament-banner-container">
                <img src={tournament.bannerUrl} alt="Tournament Banner" className="tournament-banner-image" />
              </div>
            )}

            <div className="tournament-info-grid">
              <div className="info-item">
                <span className="info-label">{t('tournament.date')}</span>
                <span className="info-value">
                  {tournament.date}
                  {new Date(tournament.date) > new Date() && ` 🕒 ${t('tournament.comingSoon')}`}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('tournament.format')}</span>
                <span className="info-value">{tournament.format}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('tournament.platform')}</span>
                <span className="info-value">{tournament.platform}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('tournament.fees')}</span>
                <span className="info-value">{tournament.fees}</span>
              </div>
            </div>

            {tournament.description && (
              <div className="tournament-description-section">
                <h3 className="description-title">{t('tournament.description')}</h3>
                <div className="description-content">
                  {tournament.description.split('\n').map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="tournament-actions-section">
              {joinedTournamentIds.includes(tournament.id) ? (
                <div className="joined-status">
                  <p className="joined-status-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    {t('tournament.joinedMessage')}
                  </p>
                  <button
                    className="action-btn view-matches-btn"
                    onClick={() => {
                      playSound('confirm');
                      navigate('/player-tournament');
                    }}
                  >
                    {t('tournament.viewMyMatches')}
                  </button>
                </div>
              ) : (
                <div className="payment-options-grid">
                  <div className="payment-option-card dp-option">
                    <h4 className="payment-option-title">Pay with DP</h4>
                    <p className="payment-option-description">Use your current DP balance</p>
                    <button
                      className={`action-btn dp-payment-btn ${tournament.started ? 'disabled' : 'pulse-animation'}`}
                      onClick={() => {
                        if (!tournament.started) {
                          setConfirmTournament(tournament);
                          setShowDpConfirmModal(true);
                        }
                      }}
                      disabled={tournament.started || joining}
                    >
                      {joining ? t('tournament.joining') : tournament.started ? t('tournament.joiningClosed') : `Pay ${tournament.dpPrice} DP`}
                    </button>
                  </div>

                  <div className="payment-divider-vertical">
                    <span>OR</span>
                  </div>

                  <div className="payment-option-card mvola-option">
                    <h4 className="payment-option-title">Pay with MVola</h4>
                    <p className="payment-option-description">Pay directly with mobile money</p>
                    <button
                      className={`action-btn mvola-payment-btn ${tournament.started ? 'disabled' : 'pulse-animation'}`}
                      onClick={() => {
                        if (!tournament.started) {
                          setSelectedTournament(tournament);
                          setShowMvolaModal(true);
                        }
                      }}
                      disabled={tournament.started || isPaymentProcessing}
                    >
                      {isPaymentProcessing ? 'Processing...' : tournament.started ? t('tournament.joiningClosed') : `Pay ${(tournament.dpPrice || 0) * 100} Ariary`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {confirmJoinTournament && (
          <div className="modal-overlay-tournament">
            <div className="modal-content-tournament">
              <h3 className="modal-title-tournament">{t('tournament.confirmJoinPrompt')}</h3>
              <p className="modal-text-tournament"><strong>{confirmJoinTournament.title}</strong></p>
              <div className="modal-actions-tournament">
                <button onClick={() => handleJoinTournamentClick(confirmJoinTournament)} className="modal-btn confirm-btn">{t('tournament.confirm')}</button>
                <button onClick={() => setConfirmJoinTournament(null)} className="modal-btn cancel-btn">{t('tournament.cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {showDpConfirmModal && (
          <div className="modal-overlay-tournament" onClick={() => setShowDpConfirmModal(false)}>
            <div className="modal-content-tournament" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-title-tournament">Confirm DP Payment</h3>
              <p className="modal-text-tournament">Are you sure you want to join this tournament?</p>
              <div className="tournament-payment-details">
                <div className="payment-detail-row">
                  <span className="detail-label">Tournament:</span>
                  <span className="detail-value">{confirmTournament?.title}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="detail-label">Participation Fee:</span>
                  <span className="detail-value">{confirmTournament?.dpPrice} DP</span>
                </div>
              </div>
              <div className="modal-actions-tournament">
                <button
                  onClick={() => {
                    setShowDpConfirmModal(false);
                    setSelectedTournament(confirmTournament);
                    handleJoinTournamentClick(confirmTournament);
                  }}
                  className="modal-btn primary-btn"
                >
                  Confirm & Join
                </button>
                <button onClick={() => setShowDpConfirmModal(false)} className="modal-btn cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}

        <div className="tournament-card">
          <div className="tournament-card-header">
            <h2 className="tournament-card-title">{t('tournament.ongoingTitle')}</h2>
          </div>

          {liveLink && (
            <div className="live-stream-toggle">
              <button className="toggle-live-btn" onClick={() => setShowLive(!showLive)}>
                {showLive ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                      <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="currentColor"/>
                    </svg>
                    {t('tournament.hideLive')}
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                    {t('tournament.showLive')}
                  </>
                )}
              </button>
            </div>
          )}

          {showLive && liveLink && (
            <div className="live-stream-container">
              <iframe
                src={liveLink}
                title="Live Stream"
                width="100%"
                height="315"
                allow="autoplay; encrypted-media"
                allowFullScreen
                frameBorder="0"
              ></iframe>
            </div>
          )}

          <div className={`bracket-embed-container ${isNativePlatform ? 'is-native' : ''}`}>
            {ongoingLink ? (
              isNativePlatform ? (
                <div className="iframe-fallback-link">
                  <p className="fallback-text">{t('tournament.iframeFallbackText')}</p>
                  <button
                    type="button"
                    className="fallback-btn"
                    onClick={() => openExternalUrl(ongoingLink)}
                  >
                    {t('tournament.openTournamentBracket')}
                  </button>
                  <a
                    href={ongoingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fallback-btn secondary"
                  >
                    {t('tournament.openInBrowser')}
                  </a>
                </div>
              ) : (
                <>
                  <iframe
                    src={ongoingLink}
                    width="100%"
                    height="500"
                    frameBorder="0"
                    scrolling="auto"
                    allowTransparency="true"
                    title="Ongoing Tournament"
                    onError={() => console.log('Iframe failed to load')}
                    onLoad={() => console.log('Iframe loaded successfully')}
                    className="tournament-bracket-iframe"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    loading="lazy"
                  />
                  <div className="iframe-fallback-link">
                    <p className="fallback-text">{t('tournament.iframeFallbackText')}</p>
                    <a
                      href={ongoingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fallback-btn"
                    >
                      {t('tournament.openTournamentBracket')}
                    </a>
                  </div>
                </>
              )
            ) : (
              <p className="loading-text">{t('tournament.loadingOngoing')}</p>
            )}
          </div>
        </div>

        <div className="tournament-card">
          <div className="tournament-card-header">
            <h2 className="tournament-card-title">{t('tournament.lastTitle')}</h2>
          </div>
          <div className={`bracket-embed-container ${isNativePlatform ? 'is-native' : ''}`}>
            {lastLink ? (
              isNativePlatform ? (
                <div className="iframe-fallback-link">
                  <p className="fallback-text">{t('tournament.iframeFallbackText')}</p>
                  <button
                    type="button"
                    className="fallback-btn"
                    onClick={() => openExternalUrl(lastLink)}
                  >
                    {t('tournament.openTournamentBracket')}
                  </button>
                  <a
                    href={lastLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fallback-btn secondary"
                  >
                    {t('tournament.openInBrowser')}
                  </a>
                </div>
              ) : (
                <>
                  <iframe
                    src={lastLink}
                    width="100%"
                    height="500"
                    frameBorder="0"
                    scrolling="auto"
                    allowTransparency="true"
                    title="Last Tournament"
                    onError={() => console.log('Last tournament iframe failed to load')}
                    onLoad={() => console.log('Last tournament iframe loaded successfully')}
                    className="tournament-bracket-iframe"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    loading="lazy"
                  />
                  <div className="iframe-fallback-link">
                    <p className="fallback-text">{t('tournament.iframeFallbackText')}</p>
                    <a
                      href={lastLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fallback-btn"
                    >
                      {t('tournament.openTournamentBracket')}
                    </a>
                  </div>
                </>
              )
            ) : (
              <p className="loading-text">{t('tournament.loadingLast')}</p>
            )}
          </div>
        </div>

        {showDpModal && (
          <div className="modal-overlay-tournament" onClick={() => setShowDpModal(false)}>
            <div className="modal-content-tournament warning-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-title-tournament">{t('tournament.insufficientDP')}</h3>
              <p className="modal-text-tournament" dangerouslySetInnerHTML={{
                __html: t('tournament.missingDPText', { dp: missingDp })
              }} />
              <div className="modal-actions-tournament">
                <button
                  onClick={() => {
                    setShowDpModal(false);
                    setShowMvolaModal(true);
                  }}
                  className="modal-btn primary-btn"
                >
                  Pay with MVola Instead
                </button>
                <button
                  onClick={() => {
                    setShowDpModal(false);
                    window.open('https://m.me/YGOMadagascarEsport', '_blank');
                  }}
                  className="modal-btn secondary-btn"
                >
                  {t('tournament.contactUs')}
                </button>
                <button onClick={() => setShowDpModal(false)} className="modal-btn cancel-btn">{t('tournament.cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {showMvolaModal && (
          <div className="modal-overlay-tournament" onClick={() => setShowMvolaModal(false)}>
            <div className="modal-content-tournament mvola-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-title-tournament">Tournament Registration via MVola</h3>
              <p className="modal-text-tournament">Pay the tournament participation fee using MVola mobile payment.</p>
              <div className="tournament-payment-details">
                <div className="payment-detail-row">
                  <span className="detail-label">Tournament:</span>
                  <span className="detail-value">{selectedTournament?.title}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="detail-label">Participation Fee:</span>
                  <span className="detail-value">{selectedTournament?.dpPrice} DP</span>
                </div>
                <div className="payment-detail-row">
                  <span className="detail-label">Amount to Pay:</span>
                  <span className="detail-value">{(selectedTournament?.dpPrice || 0) * 100} Ariary</span>
                </div>
              </div>

              <div className="phone-input-section">
                <label className="phone-input-label">Your Phone Number:</label>
                <input
                  type="tel"
                  placeholder="0341234567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="phone-number-input"
                />
              </div>

              <div className="modal-actions-tournament">
                <button
                  onClick={handleMvolaTournamentPayment}
                  disabled={isPaymentProcessing || !phoneNumber.trim()}
                  className="modal-btn primary-btn mvola-btn"
                >
                  {isPaymentProcessing ? 'Processing...' : 'Pay & Join'}
                </button>
                <button onClick={() => setShowMvolaModal(false)} className="modal-btn cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentPage;
